import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplaceService, AuthService, BillingService, OrganizationService } from "@front/m19-services";

import { IBadgeComponent, IButtonDirective, MODAL_DATA, ModalRef, ToasterService } from "@front/m19-ui";
import { AccountMarketplace, Organization } from "@front/m19-api-client";
import { AccountMarketplaceEx } from "@front/m19-models";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { map, switchMap, tap } from "rxjs/operators";

export interface SelfServiceTransferModalData {
  childOrganization: Organization;
  accountMarketplaces: AccountMarketplace[];
}

@Component({
  imports: [IBadgeComponent, IButtonDirective, TranslocoDirective],
  templateUrl: "./self-service-transfer.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelfServiceTransferModalComponent {
  private accountMarketplaceService = inject(AccountMarketplaceService);
  private organizationService = inject(OrganizationService);
  private billingService = inject(BillingService);
  private toastrService = inject(ToasterService);
  private authService = inject(AuthService);
  private translocoService = inject(TranslocoService);

  modalData = inject(MODAL_DATA) as SelfServiceTransferModalData;
  modalRef = inject(ModalRef);

  missingAccountValue = "Not found in Agency Organization";

  parentOrganization = toSignal(
    this.organizationService.organizations$.pipe(
      map((orgs: Organization[] | undefined) =>
        orgs?.find((x) => x.organizationId === this.modalData.childOrganization.parentOrganizationId),
      ),
    ),
  );
  targetAccountGroupName = toSignal(
    this.accountMarketplaceService.accountMarketplaces$.pipe(
      map((accountMarketplaces) => this.computeTargetAccountGroupNames(accountMarketplaces)),
    ),
  );
  canBeTransferred = computed(() => {
    const targetAccountGroupName = this.targetAccountGroupName();
    return (
      targetAccountGroupName !== undefined &&
      this.modalData.accountMarketplaces.every(
        (x) => targetAccountGroupName.get(x.profileId!) !== this.missingAccountValue,
      )
    );
  });
  loading = signal(false);

  private computeTargetAccountGroupNames(accountMarketplaces: AccountMarketplaceEx[] | undefined) {
    const targetAccountGroupName = new Map<number, string>();
    for (const acc of this.modalData.accountMarketplaces) {
      targetAccountGroupName.set(acc.profileId!, this.missingAccountValue);
    }
    accountMarketplaces
      ?.filter((x) => x.resourceOrganizationId === this.modalData.childOrganization.parentOrganizationId)
      .filter((x) => targetAccountGroupName.has(x.profileId!))
      .forEach((x) => targetAccountGroupName.set(x.profileId!, x.accountGroupName!));
    return targetAccountGroupName;
  }

  submit() {
    this.loading.set(true);
    this.billingService
      .transferManagementService(this.modalData.childOrganization.organizationId!)
      .pipe(
        tap({
          next: () => {
            this.toastrService.success(
              this.translocoService.translate("self-service-transfer.management_successfully_transferred"),
            );
            this.loading.set(false);
            this.modalRef.close();
          },
          error: (err) => {
            this.toastrService.error(
              err.message,
              this.translocoService.translate("self-service-transfer.management_transfer_failure"),
            );
            this.loading.set(false);
          },
        }),
        switchMap(() => this.authService.reloadUser()),
      )
      .subscribe();
  }
}
